/**
 * "Was this browser signed in the last time it managed to reach the server?"
 *
 * A **UI hint**, not a credential and not an authorization decision. It exists for exactly
 * one case, which `112-offline-drafts.md` §1 lists as ordinary: **the backend is down and
 * the user reloads the tab.** `GET /api/auth/me` then fails with a network error — not a
 * 401 — so the app cannot know whether the session is still good. Two options:
 *
 *   a) Render "Couldn't reach the server" and refuse to boot. The user's unsaved draft is
 *      sitting in IndexedDB, and we show them an error page instead of their words. That
 *      is precisely the failure this project's first design value forbids.
 *   b) Boot the shell, render the draft, keep accepting keystrokes, and let the outbox
 *      land it all when the server returns.
 *
 * We do (b). The flag is what tells us it is reasonable — it means "this browser had a
 * session, so an offline boot is a restart, not a stranger".
 *
 * **This grants nothing.** Every request is still authorized by the httpOnly session
 * cookie, server-side, on every call. If the session really did expire, the first request
 * after reconnect 401s, the outbox PAUSES (never drains), and the user signs in — with
 * their words still queued. Forging this flag in localStorage buys an attacker an empty
 * shell that 401s on contact.
 *
 * It holds no user content, so localStorage is the right home (`112` §2).
 */
const KEY = 'rn.session.known';

export function rememberSession(): void {
  localStorage.setItem(KEY, '1');
}

/** Called on a real 401 and on an explicit logout — never on a network failure. */
export function forgetSession(): void {
  localStorage.removeItem(KEY);
}

export function hadSession(): boolean {
  return localStorage.getItem(KEY) === '1';
}
